import historyHeroBackgroundSrc from '@/assets/images/about/history/history-hero-background.webp';
import history2009CorporationSrc from '@/assets/images/about/history/timeline/2009-corporation.webp';
import history2010AssociationsSrc from '@/assets/images/about/history/timeline/2010-associations.webp';
import history2011GuaranteeSrc from '@/assets/images/about/history/timeline/2011-guarantee.webp';
import history2012LicenseSrc from '@/assets/images/about/history/timeline/2012-license.webp';
import history2013IbkSrc from '@/assets/images/about/history/timeline/2013-ibk.gif';
import history2014CleanManagementSrc from '@/assets/images/about/history/timeline/2014-clean-management.webp';
import history2015CopyrightDesignSrc from '@/assets/images/about/history/timeline/2015-copyright-design.webp';
import history2016PatentSrc from '@/assets/images/about/history/timeline/2016-patent.webp';
import history2018GuntnerSrc from '@/assets/images/about/history/timeline/2018-guntner.webp';
import history2020VentureSrc from '@/assets/images/about/history/timeline/2020-venture.webp';
import history2021InnovationSrc from '@/assets/images/about/history/timeline/2021-innovation.gif';
import history2023MouSrc from '@/assets/images/about/history/timeline/2023-mou.webp';
import history2024CertificationsSrc from '@/assets/images/about/history/timeline/2024-certifications.webp';
import aboutOverviewBackgroundSrc from '@/assets/images/about/shared/overview-background.webp';
import newsImage1 from '@/assets/images/news/news_image1.webp';
import newsImage2 from '@/assets/images/news/news_image2.webp';
import newsImage3 from '@/assets/images/news/news_image3.webp';
import productImage03 from '@/assets/images/products/3.door-system.webp';
import type {
  HistoryDecadeMarker,
  HistoryEntry,
  HistoryHeroContent,
  HistorySubNavLink,
} from '@/features/aboutHistory/types';
import { routePaths } from '@/routes/routeRegistry';

const createHistoryInternalLink = (to: string, label: string): HistorySubNavLink => {
  return {
    href: to,
    label,
    to,
  };
};

const createHistoryEntry = (
  year: number,
  slug: string,
  subject: string,
  description: string,
  imageSrc: string,
  imageFit: HistoryEntry['imageFit'] = 'contain',
): HistoryEntry => {
  return {
    description,
    id: `history-${String(year)}-${slug}`,
    imageAlt: `${String(year)} ${subject} history image`,
    imageFit,
    imageSrc,
    subject,
    year,
  };
};

export const historyHeroContent: HistoryHeroContent = {
  backgroundAlt: 'Kookje TNC history page hero visual',
  backgroundSrc: historyHeroBackgroundSrc,
  description:
    'Kookje TNC has grown with a clear philosophy.\nBeyond today’s achievements, we are preparing a better tomorrow.',
  eyebrow: 'Philosophy & History',
  title: 'Kookje TNC designs\na sustainable tomorrow',
};

export const historyTimelineBackgroundSrc = aboutOverviewBackgroundSrc;

export const historySecondarySubNavLinks: HistorySubNavLink[] = [
  createHistoryInternalLink(routePaths.aboutGreeting, 'CEO Message'),
  createHistoryInternalLink(routePaths.aboutHistory, 'Philosophy & History'),
  createHistoryInternalLink(routePaths.aboutCertification, 'Patents & Certifications'),
  createHistoryInternalLink(routePaths.aboutOrganization, 'Organization'),
  createHistoryInternalLink(routePaths.aboutLocation, 'Locations'),
];

export const historyDecadeMarkers: HistoryDecadeMarker[] = [
  { endYear: 2004, key: '2000_2004', label: '2000', startYear: 2000 },
  { endYear: 2009, key: '2005_2009', label: '2005', startYear: 2005 },
  { endYear: 2014, key: '2010_2014', label: '2010', startYear: 2010 },
  { endYear: 2019, key: '2015_2019', label: '2015', startYear: 2015 },
  { endYear: 2024, key: '2020_2024', label: '2020', startYear: 2020 },
  { endYear: 2029, key: '2025_2029', label: '2025', startYear: 2025 },
];

export const historyEntries: HistoryEntry[] = [
  createHistoryEntry(
    2001,
    'founding',
    '국제냉동기계 창립',
    '· 국제냉동기계 창립',
    newsImage2,
    'cover',
  ),
  createHistoryEntry(
    2009,
    'corporation',
    '(주) 국제티엔씨로 법인 전환',
    '· (주) 국제티엔씨로 법인 전환',
    history2009CorporationSrc,
  ),
  createHistoryEntry(
    2010,
    'associations',
    '설비기술 단체 회원 가입',
    '· 한국설비기술협회 회원\n· 공조·냉동·설비연구조합 회원\n· 경기동부상공회의소 회원',
    history2010AssociationsSrc,
  ),
  createHistoryEntry(
    2011,
    'guarantee',
    '대한설비건설공제조합 회원',
    '· 대한설비건설공제조합 회원',
    history2011GuaranteeSrc,
  ),
  createHistoryEntry(
    2012,
    'license',
    '기계설비 공사업 건설업 등록증 취득',
    '· 건설업 등록증 (기계설비 공사업) 취득',
    history2012LicenseSrc,
    'contain-height',
  ),
  createHistoryEntry(
    2013,
    'ibk',
    '기업은행 패밀리 기업 인증',
    '· 기업은행 패밀리 기업 인증',
    history2013IbkSrc,
  ),
  createHistoryEntry(
    2014,
    'clean-management',
    '클린경영인증 획득',
    '· 클린경영인증 획득',
    history2014CleanManagementSrc,
    'contain-height',
  ),
  createHistoryEntry(
    2015,
    'copyright-design',
    '저작권 및 디자인 등록',
    '· 저작권 등록 (C-2015-013574호)외 10건\n· 디자인 등록 (제30-2014-0051620호)',
    history2015CopyrightDesignSrc,
    'contain-height',
  ),
  createHistoryEntry(
    2016,
    'patent',
    '특허 등록',
    '· 특허 등록 (제10-1659465호)',
    history2016PatentSrc,
    'contain-height',
  ),
  createHistoryEntry(
    2017,
    'research-quality',
    '기업부설연구소 설립 및 품질우수기업 선정',
    '· 기업부설연구소 설립\n· 구리시 우수기업 표창\n· 2017 한국품질우수기업 「냉난방공사」 부문',
    newsImage3,
    'cover',
  ),
  createHistoryEntry(
    2018,
    'guntner',
    'Guntner사 에어쿨러 공급계약',
    '· 독일 Guntner사와 냉동·냉장 에어쿨러 공급계약',
    history2018GuntnerSrc,
  ),
  createHistoryEntry(
    2019,
    'kaplanlar',
    '쇼케이스 독점 공급 계약 및 기술 인증 확대',
    '· 국립전파연구원 적합등록 (쇼케이스 Plug-In)\n· 터키 Kaplanlar사와 쇼케이스 국내 독점 공급 계약\n· 한국환경공단 냉매회수업 등록\n· 대한기계설비협회 정회원\n· NICE평가정보 기술평가우수기업 인증',
    productImage03,
  ),
  createHistoryEntry(
    2020,
    'venture',
    '일생활균형 협약파트너 지정 및 벤처기업인증',
    '· 고용노동부 일생활균형 협약파트너 지정\n· 벤처기업인증 (기술보증기금)',
    history2020VentureSrc,
  ),
  createHistoryEntry(
    2021,
    'innovation',
    '특허 등록 및 이노비즈 인증',
    '· 특허 등록 (제10-2335375호)\n· 구리지점 설립\n· 기술혁신형 중소기업 (Inno-Biz) 인증\n· 경기도지사 표창 (일자리창출)\n· 중소벤처기업진흥공단 인재육성형 중소기업 지정',
    history2021InnovationSrc,
  ),
  createHistoryEntry(
    2022,
    'relocation',
    '본사 이전 및 다산지점 설립',
    '· 본사 이전 및 다산지점 설립 (쇼룸 조성)\n· 디자인등록 (제30-1150084호)',
    newsImage3,
    'cover',
  ),
  createHistoryEntry(
    2023,
    'mou',
    '경기도 유망중소기업 선정',
    '· 경기도 유망중소기업 선정\n· 식품 매장 도어 최다 제작 파트너사 MOU 체결',
    history2023MouSrc,
  ),
  createHistoryEntry(
    2024,
    'certifications',
    '디자인·특허 등록 및 공장 등록',
    '· 기술혁신형 중소기업(Inno-Biz) 재인증\n· 디자인 등록 (제30-1271019호)\n· 특허 등록 (제10-2675817호)\n· 남양주시 공장 등록\n· 디자인 등록 (제30-1273808호)\n· 조달청 등록\n· 기술역량 우수기업 인증',
    history2024CertificationsSrc,
    'contain-height',
  ),
  createHistoryEntry(
    2025,
    'co2',
    '국내 대형마트 최초 CO₂ 시스템 설계 시공',
    '· 국내 대형마트 최초 CO₂ 시스템 설계 시공\n· Haier Carrier사 CO₂ 멀티시스템 독점 공급계약 체결\n· 남양주시 제2공장 등록\n· 실내건축 공사업 등록증 취득\n· 에너지 효율 특허제품 3종 MOU 체결\n· 롯데월드타워 공조기 필터 공급계약 체결',
    newsImage1,
  ),
];

export const historyInitialYear = historyEntries[0]?.year ?? 2001;

export const getHistoryDecadeMarker = (year: number) => {
  return (
    historyDecadeMarkers.find((marker) => year >= marker.startYear && year <= marker.endYear) ??
    historyDecadeMarkers[0]
  );
};
